/* eslint-disable react/no-array-index-key */
import { Text } from '@byecode/ui'
import { dateFormat } from '@byecode/ui/utils/dateformat'
import { CALENDAR_MODE } from '@lighthouse/core'
import { mergeRefs } from '@lighthouse/tools'
import { compareAsc, isSameDay, isSameMonth, lightFormat, startOfDay } from 'date-fns'
import React, { forwardRef, useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'

import { useLanguageContext } from '../../../../../contexts'
import type { CalendarProps } from './types'

const SCxDayBody = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`

const SCxDayRow = styled.div`
    flex: 1;
`

const SCxContent = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
`

const SCxDay = styled(Text)`
    height: 32px;
    width: 32px;
    line-height: 32px;
`

const SCxCell = styled.div<{
    hoverColor?: string
    selectedColor?: string
    hoverBackground?: string
    selectedBackgroundColor?: string
    currentMonthColor?: string
    notCurrentMonthColor?: string
}>`
    position: relative;
    padding: 4px 0;
    min-height: 32px;
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: 16px;
    text-align: center;
    color: ${props => props.currentMonthColor};

    &[data-disabled='true'] {
        opacity: 0.5;
        pointer-events: none;
    }
    &[data-selected='true'] {
        ${SCxDay} {
            border-radius: 50%;
            color: ${props => props.selectedColor};
            background-color: ${props => props.selectedBackgroundColor};
        }
    }
    &[data-out-range='true'] {
        color: ${props => props.notCurrentMonthColor};
    }

    @media (any-hover: hover) {
        &:not([data-selected='true']):hover {
            ${SCxDay} {
                border-radius: 50%;
                color: ${props => props.hoverColor};
                background-color: ${props => props.hoverBackground};
            }
        }
    }
`

interface DayCalendarProps extends CalendarProps {
    disabled?: boolean
    /**
     * @description 当前面板展示的是哪个月份的数据
     * @date 8/30/2023 - 6:51:52 PM
     *
     * @type {Date}
     */
    panelViewDate: Date

    /**
     * @description 自定义可用日期列表
     */
    customEnableDateList?: number[] | null
    /**
     * @description 对应日期描述map
     */
    dateDescribeMap?: Record<number, string>

    rows: Date[][]
}

export const DayCalendar = forwardRef<HTMLDivElement, DayCalendarProps>((props, ref) => {
    const {
        mode,
        weekShowCount,
        date,
        disabled,
        panelViewDate,
        minDate,
        maxDate,
        onChange,
        dateDescribeMap,
        customEnableDateList,
        styles,
        rows,

        ...rest
    } = props
    const { language } = useLanguageContext()

    const isDayDisabled = useCallback(
        (day: Date) => {
            if (minDate && maxDate && !isSameDay(minDate, day) && !isSameDay(maxDate, day)) {
                return compareAsc(minDate, day) > 0 || compareAsc(day, maxDate) > 0
            }
            if (minDate && !isSameDay(minDate, day)) {
                return compareAsc(minDate, day) > 0
            }
            if (maxDate && !isSameDay(maxDate, day)) {
                return compareAsc(day, maxDate) > 0
            }
            if (customEnableDateList) {
                return !customEnableDateList.some(timestamp => isSameDay(timestamp, day))
            }
            return false
        },
        [customEnableDateList, maxDate, minDate]
    )

    const isWeekMode = mode === CALENDAR_MODE.week
    const colCount = isWeekMode ? weekShowCount ?? 7 : 7

    const rootRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!date) {
            return
        }

        if (!isWeekMode) {
            return
        }

        const el = rootRef.current?.querySelector(`div[data-value="${lightFormat(date, 'yyyy/MM/dd')}"]`)
        if (!el) {
            return
        }

        el.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' })
    }, [date, isWeekMode])

    return (
        <SCxDayBody ref={mergeRefs([ref, rootRef])}>
            <SCxContent>
                {rows.map((cells, i) => (
                    <SCxDayRow key={i} style={{ display: 'flex' }}>
                        {cells.map((day, j) => (
                            <SCxCell
                                key={j}
                                onClick={() => {
                                    if (disabled || isDayDisabled(day)) {
                                        return
                                    }
                                    onChange?.(day)
                                }}
                                selectedBackgroundColor={styles?.day?.selectedBackgroundColor}
                                currentMonthColor={styles?.day.currentMonthFontColor}
                                notCurrentMonthColor={isWeekMode ? undefined : styles?.day.notCurrentMonthFontColor}
                                hoverColor={styles?.day.hoverFontColor}
                                hoverBackground={styles?.day.hoverBackgroundColor}
                                selectedColor={styles?.day.selectedFontColor}
                                data-selected={date ? isSameDay(day, date) : undefined}
                                data-out-range={!isSameMonth(day, panelViewDate) || undefined}
                                data-disabled={isDayDisabled(day)}
                                data-value={lightFormat(day, 'yyyy/MM/dd')}
                                style={{
                                    fontFamily: styles?.day?.fontFamily,
                                    flex: `0 0 ${100 / colCount}%`
                                }}
                            >
                                <SCxDay>{dateFormat(day, 'dd', { locale: language })}</SCxDay>
                                <Text size={14} whiteSpace="pre-wrap" color={styles?.day?.describeFontColor || '$colorGray500'}>
                                    {dateDescribeMap?.[startOfDay(day).getTime()]}
                                </Text>
                            </SCxCell>
                        ))}
                    </SCxDayRow>
                ))}
            </SCxContent>
        </SCxDayBody>
    )
})
